// slices/todayStudyHistory/todayStudyHistorySlice.ts
/**
 * Redux slice that stores today's study activity data only.
 * Simpler than studyHistorySlice as it only tracks the current day.
 *
 * `dateKey` records which day (in the user's timezone) the data was fetched
 * for. Consumers gate the free-plan daily limits on these totals, so they need
 * to tell today's numbers from another day's and refetch rather than gate on
 * stale ones (ORA-319).
 */
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { logout } from '../auth/authSlice';

/* ── Types ────────────────────────────────────────────── */
interface QBankData {
  correct: number;
  total: number;
  score: number;
}

interface FlashcardsData {
  total: number;
}

interface VideoData {
  started: number;
  total: number;
}

interface StudyHistoryData {
  qbank: QBankData;
  flashcards: FlashcardsData;
  videos: VideoData;
}

type StudyHistoryStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface TodayStudyHistorySliceState {
  data: StudyHistoryData;
  status: StudyHistoryStatus;
  error: string | null;
  /** Day the data was fetched for, `YYYY-MM-DD` in the user's timezone. */
  dateKey: string | null;
}

export interface TodayStudyHistoryReceivedPayload {
  data: StudyHistoryData;
  dateKey: string;
}

/* ── Helpers ──────────────────────────────────────────── */
const initialData: StudyHistoryData = {
  qbank: { correct: 0, total: 0, score: 0 },
  flashcards: { total: 0 },
  videos: { started: 0, total: 0 },
};

const initialState: TodayStudyHistorySliceState = {
  data: { ...initialData },
  status: 'idle',
  error: null,
  dateKey: null,
};

/* ── Slice ────────────────────────────────────────────── */
const todayStudyHistorySlice = createSlice({
  name: 'todayStudyHistory',
  initialState,
  reducers: {
    /* Set loading state */
    todayStudyHistoryLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },

    /* Replace entire today's data */
    todayStudyHistoryReceived: (
      state,
      { payload }: PayloadAction<TodayStudyHistoryReceivedPayload>
    ) => {
      state.data = payload.data;
      state.dateKey = payload.dateKey;
      state.status = 'succeeded';
      state.error = null;
    },

    /* Update QBank score calculation */
    updateTodayQBankScore: (state) => {
      const { correct, total } = state.data.qbank;

      state.data.qbank.score =
        total > 0 ? Math.round((correct / total) * 100) : 0;
    },

    /* Error handling */
    todayStudyHistoryFailed: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      state.status = 'failed';
      state.error = payload ?? "Failed to load today's study history";
    },

    /* Clear/reset today's data */
    clearTodayStudyHistory: (state) => {
      state.data = { ...initialData };
      state.status = 'idle';
      state.error = null;
      state.dateKey = null;
    },

    /* Increment today's QBank total */
    incrementTodayQBankTotal: (state) => {
      state.data.qbank.total += 1;
      // Recalculate score
      const { correct, total } = state.data.qbank;

      state.data.qbank.score =
        total > 0 ? Math.round((correct / total) * 100) : 0;

      state.status = 'succeeded';
    },

    /* Decrement today's QBank total (rollback helper) */
    decrementTodayQBankTotal: (state) => {
      state.data.qbank.total = Math.max(0, state.data.qbank.total - 1);
      const { correct, total } = state.data.qbank;

      state.data.qbank.score =
        total > 0 ? Math.round((correct / total) * 100) : 0;

      state.status = 'succeeded';
    },

    /* Increment today's flashcard total */
    incrementTodayFlashcardTotal: (state) => {
      state.data.flashcards.total += 1;
      state.status = 'succeeded';
    },

    /* Increment today's video total */
    incrementTodayVideoTotal: (state) => {
      state.data.videos.total += 1;
      state.data.videos.started += 1;
      state.status = 'succeeded';
    },

    /* Increment today's video started count */
    incrementTodayVideoStarted: (state) => {
      state.data.videos.started += 1;
      state.status = 'succeeded';
    },

    /* Set specific counts (useful for bulk updates) */
    setTodayQBankData: (
      state,
      { payload }: PayloadAction<Partial<QBankData>>
    ) => {
      state.data.qbank = { ...state.data.qbank, ...payload };
      // Recalculate score if total or correct changed
      if (payload.total !== undefined || payload.correct !== undefined) {
        const { correct, total } = state.data.qbank;

        state.data.qbank.score =
          total > 0 ? Math.round((correct / total) * 100) : 0;
      }
      state.status = 'succeeded';
    },

    setTodayFlashcardsData: (
      state,
      { payload }: PayloadAction<Partial<FlashcardsData>>
    ) => {
      state.data.flashcards = { ...state.data.flashcards, ...payload };
      state.status = 'succeeded';
    },

    setTodayVideoData: (
      state,
      { payload }: PayloadAction<Partial<VideoData>>
    ) => {
      state.data.videos = { ...state.data.videos, ...payload };
      state.status = 'succeeded';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.data = { ...initialData };
      state.status = 'idle';
      state.error = null;
      state.dateKey = null;
    });
  },
});

/* ── Actions ─────────────────────────────────────────── */
export const {
  todayStudyHistoryLoading,
  todayStudyHistoryReceived,
  todayStudyHistoryFailed,
  incrementTodayQBankTotal,
  decrementTodayQBankTotal,
  incrementTodayFlashcardTotal,
  incrementTodayVideoTotal,
  incrementTodayVideoStarted,
} = todayStudyHistorySlice.actions;

/* ── Selectors ───────────────────────────────────────── */
/* RootState helper type for in-file selectors */
interface SliceRoot {
  todayStudyHistory: TodayStudyHistorySliceState;
}

/* Main data selectors */
export const selectTodayStudyHistoryData = createSelector(
  [(state: SliceRoot) => state.todayStudyHistory.data],
  (data) => data
);

/* Plain selector: the value is a primitive, so there is nothing to memoize. */
export const selectTodayStudyHistoryDateKey = (state: SliceRoot) =>
  state.todayStudyHistory.dateKey;

/* ── Reducer export ──────────────────────────────────── */
export default todayStudyHistorySlice.reducer;
